// Event page headline: the first few acts on the bill, then "@ Venue". The
// <title> and the h1 are both built from this so they always match.

/** Show a third act only while the comma-joined act list stays this short. */
export const HEADLINE_ARTIST_BUDGET = 36;
const MIN_ARTISTS = 2;
const MAX_ARTISTS = 3;

export interface EventHeadlineSource {
  name: string;
  venueName: string | null | undefined;
  artistNames: string[];
}

export interface EventHeadlineParts {
  /** Acts to print, in bill order. Empty when the event has no linked acts. */
  artists: string[];
  /** Acts left off the headline. */
  more: number;
}

function normalize(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

/**
 * Bill order: acts named in the event title come first, in the order the
 * title lists them (the source usually leads with the headliner); the rest
 * follow in the order they were given. Duplicates and blanks are dropped.
 */
export function orderHeadlineArtists(artistNames: string[], eventName: string): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of artistNames) {
    const name = normalize(raw);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    unique.push(name);
  }
  const title = eventName.toLowerCase();
  const isWordChar = (char: string | undefined) => !!char && /[\p{L}\p{N}]/u.test(char);
  // Whole-word position of the act in the title, so "Low" never matches
  // inside "Lowell".
  const position = (name: string) => {
    const needle = name.toLowerCase();
    let from = 0;
    while (from <= title.length) {
      const at = title.indexOf(needle, from);
      if (at === -1) break;
      const before = title[at - 1];
      const after = title[at + needle.length];
      if (!(isWordChar(before) && isWordChar(needle[0])) && !(isWordChar(after) && isWordChar(needle[needle.length - 1]))) {
        return at;
      }
      from = at + 1;
    }
    return Number.POSITIVE_INFINITY;
  };
  return unique
    .map((name, index) => ({ name, index, at: position(name) }))
    .sort((a, b) => a.at - b.at || a.index - b.index)
    .map((entry) => entry.name);
}

/** Pick two acts, or three when all three names fit the budget together. */
export function pickHeadlineArtists(artistNames: string[], eventName: string): EventHeadlineParts {
  const ordered = orderHeadlineArtists(artistNames, eventName);
  const artists: string[] = [];
  for (const name of ordered) {
    if (artists.length >= MAX_ARTISTS) break;
    const fits = [...artists, name].join(", ").length <= HEADLINE_ARTIST_BUDGET;
    if (artists.length >= MIN_ARTISTS && !fits) break;
    artists.push(name);
  }
  return { artists, more: ordered.length - artists.length };
}

/**
 * "Act A, Act B @ Venue". Acts past the first two or three are left off
 * without a count. Falls back to the event's own name when it has no linked
 * acts.
 */
export function formatEventHeadline(source: EventHeadlineSource): string {
  const { artists } = pickHeadlineArtists(source.artistNames, source.name);
  const lead = artists.length > 0 ? artists.join(", ") : normalize(source.name);
  const venue = source.venueName ? normalize(source.venueName) : "";
  return venue ? `${lead} @ ${venue}` : lead;
}
